import { useCallback } from "react";
import { ScrollView } from "react-native";
import { useRouter, useLocalSearchParams, usePathname } from "expo-router";
import { View, Text } from "@src/components";
import { useCamerasListState, type CameraApiResponse } from "@src/features/cameras";
import { getCameraKeyString } from "@src/utils/key-parser";
import { CameraListItem } from "@src/features/cameras";
import { SidepanelSiteGroup } from "./SidepanelSiteGroup.web";
import { track } from "@visioni/a3s-analytics";
import { CameraClicked, cameraClickedProps } from "@src/features/cameras/analytics";
import { useCameraCollection } from "@src/features/cameras/hooks/useCameraCollection";
import { useSiteNames } from "@src/features/cameras/hooks/useSiteNames";
import { CameraListItemSidepanel } from "@src/features/cameras/components/CameraListItemSidepanel.web";
import { useSnapshots } from "@visioni/a3s-media/app";

interface SidepanelCameraListProps {
  collapsed?: boolean;
  activeCameraKey?: string;
}

export function SidepanelCameraList({ collapsed = false, activeCameraKey }: SidepanelCameraListProps) {
  const router = useRouter();
  const pathname = usePathname();
  const params = useLocalSearchParams<{ cameraKey?: string; siteId?: string }>();

  const siteId = params.siteId;

  const listState = useCamerasListState(siteId);
  const { data: snapshots } = useSnapshots({});
  const siteNames = useSiteNames();

  const allCameras = listState.status === "success" ? listState.cameras : undefined;

  const { groupedCameras } = useCameraCollection({
    cameras: allCameras,
    searchQuery: "",
    sortBy: null,
    siteNames,
  });

  // Determine navigation context - are we on camera details or alarm settings?
  const isAlarmSettingsScreen = pathname.includes("/alarm");

  const handleCameraSelect = useCallback(
    (camera: CameraApiResponse) => {
      const cameraKey = getCameraKeyString(camera);
      const cameraTenantId = camera.site.tenant;
      const cameraSiteId = camera.site.id;

      track(CameraClicked, cameraClickedProps(camera));

      if (isAlarmSettingsScreen) {
        // Navigate to alarm settings for the selected camera
        router.push(`/tenants/${cameraTenantId}/sites/${cameraSiteId}/cameras/${cameraKey}/alarm` as never);
      } else {
        // Navigate to camera details
        router.push(`/tenants/${cameraTenantId}/sites/${cameraSiteId}/cameras/${cameraKey}` as never);
      }
    },
    [router, isAlarmSettingsScreen],
  );

  if (listState.status === "pending") {
    return (
      <View flex={1} justifyContent="center" alignItems="center" py="6xl">
        <Text textStyle="sm/regular" color="textMuted">
          Loading cameras...
        </Text>
      </View>
    );
  }

  if (listState.status === "error") {
    return (
      <View flex={1} justifyContent="center" alignItems="center" py="6xl">
        <Text textStyle="sm/regular" color="textMuted">
          Failed to load cameras
        </Text>
      </View>
    );
  }

  // Empty state
  if (groupedCameras.length === 0) {
    return (
      <View flex={1} justifyContent="center" alignItems="center" py="6xl">
        <Text textStyle="sm/regular" color="textMuted">
          {"No cameras available"}
        </Text>
      </View>
    );
  }

  const hasSingleSite = groupedCameras.length === 1;

  // Single site: flat list without site grouping
  if (hasSingleSite) {
    const cameras = groupedCameras[0].cameras;
    return (
      <ScrollView style={{ flex: 1 }} showsVerticalScrollIndicator={false}>
        <View>
          {cameras.map((camera) => {
            const cameraKey = getCameraKeyString(camera);
            const isSelected = cameraKey === activeCameraKey;
            return (
              <CameraListItemSidepanel
                key={cameraKey}
                camera={camera}
                siteId={camera.site.id}
                onSelect={() => handleCameraSelect(camera)}
                href={
                  isAlarmSettingsScreen ?
                    (`/tenants/${camera.site.tenant}/sites/${camera.site.id}/cameras/${encodeURIComponent(cameraKey)}/alarm` as never)
                  : (`/tenants/${camera.site.tenant}/sites/${camera.site.id}/cameras/${encodeURIComponent(cameraKey)}` as never)
                }
                isSelected={isSelected}
                collapsed={collapsed}
                snapshot={snapshots?.[`${camera.key.asset}@${camera.key.id}`]}
              />
            );
          })}
        </View>
      </ScrollView>
    );
  }

  // Multiple sites: grouped view with site headers
  return (
    <ScrollView style={{ flex: 1 }} showsVerticalScrollIndicator={false}>
      <View>
        {groupedCameras.map((group, index) => {
          const containsSelectedCamera = group.cameras.some((camera) => getCameraKeyString(camera) === activeCameraKey);

          return (
            <SidepanelSiteGroup
              key={group.siteId}
              siteName={group.siteName}
              siteSubtitle={group.siteSubtitle}
              thumbnailUrl={group.thumbnailUrl}
              cameras={group.cameras}
              selectedCameraKey={activeCameraKey}
              onCameraSelect={handleCameraSelect}
              isAlarmSettingsScreen={isAlarmSettingsScreen}
              defaultExpanded={containsSelectedCamera || index === 0}
              collapsed={collapsed}
              snapshots={snapshots}
            />
          );
        })}
      </View>
    </ScrollView>
  );
}
